/**
 * The badges over a review card: its kind, for a contrast card the moments it spans
 * ("Moment 2 · 3", joined with a middle dot because a badge is not prose, so no
 * `Intl.ListFormat`), and the "Swedish only" marker. Also used by the "Nästa kort" panel, which
 * shows kind and moment but never a card's text.
 */
import { TranslationPendingBadge } from "@client/components/SourceBadge";
import { Badge } from "@client/components/ui";
import { useTopicsAndChapters } from "@client/api/topics";
import { useT } from "@client/i18n";
import type { QueueCardDto } from "./types";

type BadgeCard = Pick<QueueCardDto, "kind" | "chapter" | "contrastWith" | "translationPending">;

/** `Moment 2 · 3` for a contrast card: its own moment, then those of the topics it is set against. */
function ContrastMoments({ card }: { card: BadgeCard }) {
  const t = useT();
  // Fetched only for a contrast card; without the topics it shows the card's own moment.
  const topics = useTopicsAndChapters().data?.topics;
  const chapters = [card.chapter];
  for (const id of card.contrastWith ?? []) {
    const chapter = topics?.find((topic) => topic.id === id)?.chapter;
    if (chapter !== undefined && !chapters.includes(chapter)) chapters.push(chapter);
  }
  return (
    <Badge tone="accent">
      {t("flashcards.review.chapters", { chapters: chapters.join(" · ") })}
    </Badge>
  );
}

export function CardBadges({ card }: { card: BadgeCard }) {
  const t = useT();
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone="neutral">{t(`flashcards.review.kind.${card.kind}`)}</Badge>
      {card.kind === "contrast" && <ContrastMoments card={card} />}
      <TranslationPendingBadge pending={card.translationPending} />
    </span>
  );
}

/** A queue-panel entry: the moment and the kind, nothing of the card's front or back. */
export function PanelBadges({ card }: { card: BadgeCard }) {
  const t = useT();
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone="neutral">{t("flashcards.review.chapters", { chapters: card.chapter })}</Badge>
      <Badge tone="neutral">{t(`flashcards.review.kind.${card.kind}`)}</Badge>
    </span>
  );
}
